import React, { useEffect, useState, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';
import JsBarcode from 'jsbarcode';

const PKG_STATUS = {
  active:     { bg: C.successBg, color: C.successText, label: 'Active'     },
  dispatched: { bg: C.infoBg,    color: C.infoText,    label: 'Dispatched' },
  installed:  { bg: '#EDE9FE',   color: '#5B21B6',     label: 'Installed'  },
};

const SLOT_COLORS = ['#F5A623', '#8B5CF6', '#3B82F6'];

function identifierMeta(categoryName = '') {
  const n = categoryName.toLowerCase();
  if (n.includes('sim'))                                       return { label: 'SIM / Phone No.',    placeholder: 'e.g. 9876543210'       };
  if (n.includes('device') || n.includes('gps') || n.includes('tracker')) return { label: 'IMEI Number', placeholder: 'e.g. 358240051111110' };
  if (n.includes('software') || n.includes('license'))         return { label: 'License Key',        placeholder: 'e.g. XXXX-XXXX-XXXX'   };
  return { label: 'Serial Number', placeholder: 'Serial number' };
}

export default function Package() {
  const [packages,  setPackages]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [toast,     setToast]     = useState(null);
  const [modal,     setModal]     = useState(null);
  const [selPkg,         setSelPkg]         = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [dispatchHistory, setDispatchHistory] = useState([]);
  const [historyLoading,  setHistoryLoading]  = useState(false);

  // ── Create wizard ────────────────────────────────────────────────────────────
  const [step,         setStep]         = useState(1);       // 1 = pick cats, 2 = enter IMEIs
  const [categories,   setCategories]   = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);      // ordered array of category objects
  const [catEntries,   setCatEntries]   = useState({});      // { catId: { query, result, error, searching } }
  const [pkgName,      setPkgName]      = useState('');
  const [pkgNotes,     setPkgNotes]     = useState('');
  const [pkgNameSource, setPkgNameSource] = useState('custom'); // 'custom' | catId number
  const catInputRefs   = useRef({});
  const barcodeCanvasRef = useRef(null);
  const [barcodeData, setBarcodeData] = useState(null); // { serial, label, packageId }

  // Derive package name from a confirmed slot's item
  const getAutoName = (catId) => {
    const item = catEntries[catId]?.result;
    if (!item) return '';
    const serial = item.serial_number || item.item_id || '';
    return item.type_name ? `${item.type_name} (${serial})` : serial;
  };
  const effectivePkgName = pkgNameSource === 'custom' ? pkgName.trim() : getAutoName(pkgNameSource);

  // Filters
  const [search,   setSearch]   = useState('');
  const [stFilter, setStFilter] = useState('');

  // Assign to installer
  const [installers,      setInstallers]      = useState([]);
  const [assignPkg,       setAssignPkg]       = useState(null);
  const [assignInstaller, setAssignInstaller] = useState('');
  const [assigning,       setAssigning]       = useState(false);
  const [assignErr,       setAssignErr]       = useState('');

  const showToast = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 3500); };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/packages'); setPackages(r.data); }
    catch { /* 401 handled by axios interceptor; other errors shown silently */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Installers for the assign picker
  useEffect(() => {
    api.get('/api/users', { params: { role: 'installer' } })
      .then(r => setInstallers((r.data || []).filter(u => u.role === 'installer')))
      .catch(() => {});
  }, []);

  // ── Assign to installer ──────────────────────────────────────────────────────
  const openAssign = (p) => { setAssignPkg(p); setAssignInstaller(''); setAssignErr(''); };
  const confirmAssign = async () => {
    if (!assignInstaller) { setAssignErr('Select an installer.'); return; }
    setAssigning(true); setAssignErr('');
    try {
      const r = await api.post(`/api/packages/${assignPkg.id}/assign`, { installer_id: Number(assignInstaller) });
      setAssignPkg(null);
      await loadAll();
      setToast({ type: 'success', text: r.data.message });
      setTimeout(() => setToast(null), 3500);
    } catch (e) {
      setAssignErr(e?.response?.data?.detail || 'Assign failed.');
    } finally { setAssigning(false); }
  };

  // ── Open create ──────────────────────────────────────────────────────────────

  const openCreate = async () => {
    setStep(1); setSelectedCats([]); setCatEntries({});
    setPkgName(''); setPkgNotes(''); setPkgNameSource('custom'); setSaving(false);
    try { const r = await api.get('/api/categories'); setCategories(r.data); }
    catch { setCategories([]); }
    setModal('create');
  };

  // ── Step 1: category toggle ──────────────────────────────────────────────────

  const toggleCat = (cat) => {
    setSelectedCats(prev => {
      const already = prev.find(c => c.id === cat.id);
      if (already) return prev.filter(c => c.id !== cat.id);
      if (prev.length >= 3) return prev;
      return [...prev, cat];
    });
  };

  const goToStep2 = () => {
    // Init catEntries for each selected cat
    const init = {};
    selectedCats.forEach(c => { init[c.id] = { query: '', result: null, error: '', searching: false }; });
    setCatEntries(init);
    setPkgNameSource('custom');
    setStep(2);
    setTimeout(() => {
      const firstCatId = selectedCats[0]?.id;
      if (firstCatId) catInputRefs.current[firstCatId]?.focus();
    }, 80);
  };

  // ── Step 2: per-category IMEI search ────────────────────────────────────────

  const updateEntry = (catId, patch) =>
    setCatEntries(p => ({ ...p, [catId]: { ...p[catId], ...patch } }));

  const searchForCat = async (catId) => {
    const entry = catEntries[catId] || {};
    const q = (entry.query || '').trim();
    if (!q) return;
    updateEntry(catId, { searching: true, result: null, error: '' });
    try {
      const r = await api.get('/api/inventory/by-serial', { params: { q } });
      const item = r.data;

      // Must belong to this category
      if (item.category_id !== catId) {
        const cat = selectedCats.find(c => c.id === catId);
        updateEntry(catId, { searching: false, error: `This serial is from "${item.category_name}", not "${cat?.name}"` });
        return;
      }
      // Must be in-stock
      if (item.status === 'packed') {
        updateEntry(catId, { searching: false, error: `${item.item_id} is already packed in another package` });
        return;
      }
      if (item.status !== 'in-stock') {
        updateEntry(catId, { searching: false, error: `${item.item_id} is not in-stock (status: ${item.status})` });
        return;
      }
      // No duplicates across other category slots
      const usedElsewhere = Object.entries(catEntries).some(
        ([cid, e]) => Number(cid) !== catId && e.result?.id === item.id
      );
      if (usedElsewhere) {
        updateEntry(catId, { searching: false, error: 'This item is already used in another slot' });
        return;
      }

      updateEntry(catId, { searching: false, result: item, error: '' });

      // Auto-select this slot as package name source if none chosen yet
      setPkgNameSource(prev => prev === 'custom' ? catId : prev);

      // Auto-move focus to the next unfilled category
      const nextCat = selectedCats.find(c => c.id !== catId && !catEntries[c.id]?.result);
      if (nextCat) setTimeout(() => catInputRefs.current[nextCat.id]?.focus(), 60);

    } catch (e) {
      updateEntry(catId, { searching: false, error: e?.response?.data?.detail || 'Not found' });
    }
  };

  const clearResult = (catId) => {
    updateEntry(catId, { result: null, query: '', error: '' });
    if (pkgNameSource === catId) setPkgNameSource('custom');
  };

  // ── Save ─────────────────────────────────────────────────────────────────────

  const allFilled   = selectedCats.length >= 2 && selectedCats.every(c => catEntries[c.id]?.result);
  const itemIds     = selectedCats.map(c => catEntries[c.id]?.result?.id).filter(Boolean);

  const savePackage = async () => {
    if (!allFilled) return;
    setSaving(true);
    try {
      const { data: created } = await api.post('/api/packages', {
        name:               effectivePkgName || null,
        inventory_item_ids: itemIds,
        notes:              pkgNotes.trim() || null,
      });
      await loadAll();
      showToast('success', 'Package created successfully.');

      // Show barcode for the radio-selected slot's serial number
      const sourceItem = pkgNameSource !== 'custom' ? catEntries[pkgNameSource]?.result : null;
      if (sourceItem?.serial_number) {
        setBarcodeData({
          serial:    sourceItem.serial_number,
          label:     effectivePkgName || sourceItem.item_id,
          packageId: created?.package_id,
        });
        setModal('barcode');
      } else {
        setModal(null);
      }
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/packages/${selPkg.id}`);
      await loadAll();
      showToast('success', `${selPkg.package_id} deleted — items restored to In Stock.`);
      setModal(null);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally { setSaving(false); }
  };

  const openHistory = async (pkg) => {
    setSelPkg(pkg);
    setDispatchHistory([]);
    setHistoryLoading(true);
    setModal('history');
    try {
      const r = await api.get(`/api/packages/${pkg.id}/dispatch-history`);
      setDispatchHistory(r.data);
    } catch { setDispatchHistory([]); }
    finally { setHistoryLoading(false); }
  };

  // ── Barcode ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (modal === 'barcode' && barcodeData?.serial && barcodeCanvasRef.current) {
      try {
        JsBarcode(barcodeCanvasRef.current, barcodeData.serial, {
          format:       'CODE128',
          width:        2.2,
          height:       90,
          displayValue: true,
          fontSize:     13,
          font:         'monospace',
          textMargin:   6,
          margin:       18,
          background:   '#ffffff',
          lineColor:    '#111111',
        });
      } catch { /* invalid barcode value — canvas stays blank */ }
    }
  }, [modal, barcodeData]);

  const downloadBarcode = () => {
    const canvas = barcodeCanvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${(barcodeData.label || barcodeData.serial).replace(/[^a-z0-9]/gi, '_')}.png`;
    a.click();
  };

  const printBarcode = () => {
    const canvas = barcodeCanvasRef.current;
    if (!canvas) return;
    const img = canvas.toDataURL('image/png');
    const win = window.open('', '_blank', 'width=480,height=360');
    win.document.write(`<!DOCTYPE html><html><head>
      <title>Barcode — ${barcodeData.label || barcodeData.serial}</title>
      <style>
        body { margin: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; font-family: monospace; background: #fff; }
        img  { max-width: 100%; }
        .lbl { font-size: 14px; font-weight: bold; margin-top: 8px; color: #111; }
        .sub { font-size: 11px; color: #666; margin-top: 3px; }
      </style>
    </head><body>
      <img src="${img}" />
      <div class="lbl">${barcodeData.label || ''}</div>
      <div class="sub">${barcodeData.packageId || ''}</div>
    </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 350);
  };

  // ── Filters ──────────────────────────────────────────────────────────────────

  const filtered = packages.filter(p => {
    const q = search.toLowerCase();
    return (
      (!q || p.package_id?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q)
        || p.items?.some(i => i.serial_number?.toLowerCase().includes(q)))
      && (!stFilter || p.status === stFilter)
    );
  });

  const counts = {
    total:      packages.length,
    active:     packages.filter(p => p.status === 'active').length,
    dispatched: packages.filter(p => p.status === 'dispatched').length,
    installed:  packages.filter(p => p.status === 'installed').length,
  };

  return (
    <Layout title="Package">

      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border: `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'Total Packages', val: counts.total,      dot: C.primary },
          { label: 'Active',         val: counts.active,     dot: C.success },
          { label: 'Dispatched',     val: counts.dispatched, dot: C.info    },
          { label: 'Installed',      val: counts.installed,  dot: '#7C3AED' },
        ].map(({ label, val, dot }) => (
          <div key={label} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: C.textMain }}>{val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
        <button onClick={openCreate} style={{ ...btnPrimary, marginLeft: 'auto' }}>+ Create Package</button>
      </div>

      {/* Table */}
      <div style={card}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search by ID, name, or IMEI…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: '200px', ...inputBase }} />
          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={selS}>
            <option value="">All Status</option>
            {Object.entries(PKG_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>

          {(search || stFilter) && <button onClick={() => { setSearch(''); setStFilter(''); }} style={btnGhost}>Clear</button>}
          <span style={{ fontSize: '12px', color: C.textSub }}>{filtered.length} / {packages.length}</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['Package ID', 'Name', 'Items', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={emptyCell}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} style={emptyCell}>
                  {packages.length === 0 ? 'No packages yet. Click "+ Create Package" to start.' : 'No results match your filter.'}
                </td></tr>
              ) : filtered.map((p, i) => {
                const ss = PKG_STATUS[p.status] || PKG_STATUS.active;
                return (
                  <tr key={p.id} style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}` }}>
                    <td style={td}>
                      <span onClick={() => { setSelPkg(p); setModal('view'); }}
                        style={{ fontFamily: 'monospace', fontWeight: 700, color: C.primaryDeep, cursor: 'pointer', textDecoration: 'underline dotted' }}>
                        {p.package_id}
                      </span>
                    </td>
                    <td style={td}>{p.name || <span style={{ color: C.textSub }}>—</span>}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        {p.items.map((it, idx) => (
                          <div key={it.inventory_item_id} style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: `${SLOT_COLORS[idx]}18`, border: `1px solid ${SLOT_COLORS[idx]}40`,
                            borderRadius: '6px', padding: '3px 8px',
                          }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: SLOT_COLORS[idx] }}>{it.category_name}</span>
                            <span style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textMain }}>{it.serial_number || it.item_id}</span>
                            {it.ref_number && <span style={{ fontFamily: 'monospace', fontSize: '10px', color: C.textSub }}>· {it.ref_number}</span>}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td style={td}>
                      <div>
                        <span style={{ background: ss.bg, color: ss.color, padding: '3px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>{ss.label}</span>
                        {p.status === 'installed' && p.mapped_client_name && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px' }}>
                            <span style={{ fontSize: '10px', color: '#7C3AED', fontWeight: 700 }}>👤 {p.mapped_client_name}</span>
                            <span style={{ fontSize: '10px', color: C.textSub }}>({p.mapped_client_ref})</span>
                          </div>
                        )}
                        {p.status !== 'installed' && p.assigned_installer_name && (
                          <div style={{ fontSize: '10px', color: '#1D4ED8', fontWeight: 700, marginTop: '5px' }}>
                            👷 {p.assigned_installer_name}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={td}><span style={{ fontSize: '12px', color: C.textSub }}>{p.created_at}</span></td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        <SmBtn label="View" color="#8B5CF6" onClick={() => { setSelPkg(p); setModal('view'); }} />
                        {p.status !== 'installed' && (
                          <SmBtn label={p.assigned_installer_name ? 'Reassign' : 'Assign'} color="#1D4ED8" onClick={() => openAssign(p)} />
                        )}
                        <SmBtn label="History" color={C.info} onClick={() => openHistory(p)} />
                        <SmBtn label="Delete" color={C.danger} onClick={() => { setSelPkg(p); setModal('delete'); }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Create Package Modal ── */}
      {modal === 'create' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '560px' }}>

            {/* Header with step indicator */}
            <div style={{ padding: '20px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
                <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>
                  {step === 1 ? 'Step 1 — Select Categories' : 'Step 2 — Enter Identifiers'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Step pills */}
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {[1, 2].map(s => (
                    <div key={s} style={{
                      height: '8px', borderRadius: '4px', transition: 'width 0.25s, background 0.2s',
                      width: s === step ? '28px' : '8px',
                      background: s <= step ? C.primary : C.border,
                    }} />
                  ))}
                </div>
                <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
              </div>
            </div>

            <div style={{ padding: '22px 28px', overflowY: 'auto', maxHeight: '70vh' }}>

              {/* ── STEP 1: category cards ── */}
              {step === 1 && (
                <>
                  <p style={{ margin: '0 0 16px', fontSize: '13px', color: C.textSub, lineHeight: 1.5 }}>
                    Choose <strong>2 or 3 categories</strong> to combine into one package. The order you select is the slot order.
                  </p>

                  {categories.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: C.textSub }}>No categories found.</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
                      {categories.map(cat => {
                        const selIdx = selectedCats.findIndex(c => c.id === cat.id);
                        const selected = selIdx !== -1;
                        const color = selected ? SLOT_COLORS[selIdx] : null;
                        return (
                          <div
                            key={cat.id}
                            onClick={() => toggleCat(cat)}
                            style={{
                              position: 'relative', padding: '16px 14px', borderRadius: '12px', cursor: 'pointer',
                              border: `2px solid ${selected ? color : C.border}`,
                              background: selected ? `${color}12` : C.white,
                              transition: 'all 0.15s', textAlign: 'center',
                            }}
                          >
                            {/* Slot number badge */}
                            {selected && (
                              <div style={{
                                position: 'absolute', top: '-8px', right: '-8px',
                                width: '22px', height: '22px', borderRadius: '50%',
                                background: color, color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '11px', fontWeight: 800,
                              }}>{selIdx + 1}</div>
                            )}
                            <div style={{ fontSize: '24px', marginBottom: '6px' }}>📂</div>
                            <div style={{ fontSize: '12px', fontWeight: selected ? 700 : 500, color: selected ? color : C.textMain, lineHeight: 1.3 }}>
                              {cat.name}
                            </div>
                            {cat.product_name && (
                              <div style={{ fontSize: '10px', color: C.textSub, marginTop: '3px' }}>{cat.product_name}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Selection summary */}
                  {selectedCats.length > 0 && (
                    <div style={{ marginTop: '16px', padding: '12px 16px', background: C.sidebar, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {selectedCats.map((cat, idx) => (
                        <React.Fragment key={cat.id}>
                          <span style={{ background: `${SLOT_COLORS[idx]}30`, color: SLOT_COLORS[idx], padding: '3px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 700 }}>
                            {cat.name}
                          </span>
                          {idx < selectedCats.length - 1 && <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '16px' }}>+</span>}
                        </React.Fragment>
                      ))}
                      {selectedCats.length >= 2 && (
                        <span style={{ marginLeft: 'auto', color: C.primary, fontSize: '12px', fontWeight: 600 }}>= 1 Package</span>
                      )}
                    </div>
                  )}

                  {selectedCats.length < 2 && (
                    <div style={{ marginTop: '12px', fontSize: '12px', color: C.textSub }}>ℹ Select at least 2 categories.</div>
                  )}
                </>
              )}

              {/* ── STEP 2: per-category IMEI entry ── */}
              {step === 2 && (
                <>
                  {/* Package name */}
                  <div style={{ marginBottom: '18px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>Package Name (optional)</label>
                    {pkgNameSource !== 'custom' ? (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          value={getAutoName(pkgNameSource)}
                          readOnly
                          style={{ ...inputBase, flex: 1, background: `${SLOT_COLORS[selectedCats.findIndex(c => c.id === pkgNameSource)]}0D`, fontWeight: 600, color: C.textMain, cursor: 'default' }}
                        />
                        <button onClick={() => setPkgNameSource('custom')} style={{ ...btnGhost, padding: '9px 14px', fontSize: '12px', flexShrink: 0 }}>
                          ✎ Edit
                        </button>
                      </div>
                    ) : (
                      <input value={pkgName} onChange={e => setPkgName(e.target.value)}
                        placeholder="e.g. GPS Kit A — or select an item below to auto-fill" style={inputBase} />
                    )}
                  </div>

                  {/* Per-category entry */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
                    {selectedCats.map((cat, idx) => {
                      const entry  = catEntries[cat.id] || {};
                      const meta   = identifierMeta(cat.name);
                      const color  = SLOT_COLORS[idx];
                      const hasResult = !!entry.result;
                      return (
                        <div key={cat.id} style={{
                          border: `2px solid ${hasResult ? color : entry.error ? C.danger : C.border}`,
                          borderRadius: '12px', overflow: 'hidden', transition: 'border-color 0.15s',
                        }}>
                          {/* Category label bar */}
                          <div style={{ padding: '10px 14px', background: hasResult ? `${color}15` : '#F9FAFB', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '11px' }}>
                              {idx + 1}
                            </div>
                            <span style={{ fontWeight: 700, fontSize: '13px', color: hasResult ? color : C.textMain }}>{cat.name}</span>
                            <span style={{ fontSize: '11px', color: C.textSub, marginLeft: 'auto' }}>{meta.label}</span>
                          </div>

                          <div style={{ padding: '12px 14px' }}>
                            {/* Found item card */}
                            {hasResult ? (
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, color: color, fontSize: '12px' }}>
                                      {entry.result.item_id}
                                      {entry.result.type_name && <span style={{ marginLeft: '8px', fontWeight: 400, color: C.textSub }}>›  {entry.result.type_name}</span>}
                                    </div>
                                    <div style={{ fontFamily: 'monospace', fontSize: '13px', color: C.textMain, marginTop: '2px', fontWeight: 600 }}>
                                      {entry.result.serial_number}
                                    </div>
                                    {entry.result.ref_number && (
                                      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub, marginTop: '1px' }}>Ref: {entry.result.ref_number}</div>
                                    )}
                                    <div style={{ fontSize: '11px', color: C.success, marginTop: '3px', fontWeight: 600 }}>✓ In Stock — ready to pack</div>
                                  </div>
                                  <button onClick={() => clearResult(cat.id)} style={{
                                    background: C.dangerBg, color: C.danger, border: 'none', borderRadius: '6px',
                                    padding: '5px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, flexShrink: 0,
                                  }}>Change</button>
                                </div>
                                {/* Radio: use this item's info as the package name */}
                                <label htmlFor={`radio-${cat.id}`} style={{
                                  display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px',
                                  paddingTop: '8px', borderTop: `1px dashed ${color}30`,
                                  cursor: 'pointer',
                                }}>
                                  <input
                                    type="radio"
                                    id={`radio-${cat.id}`}
                                    name="pkgNameSource"
                                    checked={pkgNameSource === cat.id}
                                    onChange={() => setPkgNameSource(cat.id)}
                                    style={{ accentColor: color, width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                                  />
                                  <span style={{ fontSize: '11px', color: pkgNameSource === cat.id ? color : C.textSub, fontWeight: pkgNameSource === cat.id ? 700 : 400 }}>
                                    Use as package name
                                    {pkgNameSource === cat.id && (
                                      <span style={{ marginLeft: '6px', fontFamily: 'monospace', color: C.textMain }}>
                                        → {getAutoName(cat.id)}
                                      </span>
                                    )}
                                  </span>
                                </label>
                              </div>
                            ) : (
                              <>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <input
                                    ref={el => catInputRefs.current[cat.id] = el}
                                    value={entry.query || ''}
                                    onChange={e => updateEntry(cat.id, { query: e.target.value, error: '', result: null })}
                                    onKeyDown={e => e.key === 'Enter' && searchForCat(cat.id)}
                                    placeholder={`Scan or type ${meta.placeholder}…`}
                                    style={{ ...inputBase, flex: 1, fontFamily: 'monospace', fontSize: '13px', borderColor: entry.error ? C.danger : C.border }}
                                  />
                                  <button
                                    onClick={() => searchForCat(cat.id)}
                                    disabled={!entry.query?.trim() || entry.searching}
                                    style={{ ...btnPrimary, padding: '9px 16px', fontSize: '12px', flexShrink: 0, opacity: !entry.query?.trim() ? 0.5 : 1 }}
                                  >
                                    {entry.searching ? '…' : 'Find'}
                                  </button>
                                </div>
                                {entry.error && (
                                  <div style={{ marginTop: '6px', fontSize: '12px', color: C.danger, fontWeight: 600 }}>⚠ {entry.error}</div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Notes */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>Notes (optional)</label>
                    <textarea value={pkgNotes} onChange={e => setPkgNotes(e.target.value)}
                      placeholder="Any notes about this package…" rows={2}
                      style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>

                  {/* Progress hint */}
                  {!allFilled && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: C.textSub }}>
                      ℹ {selectedCats.filter(c => catEntries[c.id]?.result).length} of {selectedCats.length} identifiers confirmed.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                {step === 2 && (
                  <button onClick={() => setStep(1)} style={btnGhost}>← Back</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
                {step === 1 && (
                  <button onClick={goToStep2} disabled={selectedCats.length < 2} style={{ ...btnPrimary, opacity: selectedCats.length < 2 ? 0.5 : 1 }}>
                    Next → ({selectedCats.length} selected)
                  </button>
                )}
                {step === 2 && (
                  <button onClick={savePackage} disabled={saving || !allFilled} style={{ ...btnPrimary, opacity: !allFilled ? 0.5 : 1 }}>
                    {saving ? 'Saving…' : `🎁 Create Package`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── View Modal ── */}
      {modal === 'view' && selPkg && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '500px' }}>
            <div style={{ padding: '20px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ width: '3px', height: '22px', background: C.primary, borderRadius: '2px' }} />
                <div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 800, color: C.textMain, fontSize: '16px' }}>{selPkg.package_id}</div>
                  {selPkg.name && <div style={{ fontSize: '12px', color: C.textSub }}>{selPkg.name}</div>}
                </div>
                <span style={{ background: PKG_STATUS[selPkg.status]?.bg, color: PKG_STATUS[selPkg.status]?.color, padding: '3px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>
                  {PKG_STATUS[selPkg.status]?.label}
                </span>
                {selPkg.status === 'installed' && selPkg.mapped_client_name && (
                  <span style={{ background: '#EDE9FE', color: '#5B21B6', padding: '3px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>
                    👤 {selPkg.mapped_client_name} ({selPkg.mapped_client_ref})
                  </span>
                )}
              </div>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>
            <div style={{ padding: '20px 28px' }}>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                Packed Items ({selPkg.item_count})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {selPkg.items.map((it, idx) => (
                  <div key={it.inventory_item_id} style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '10px',
                    background: `${SLOT_COLORS[idx]}0D`, border: `1.5px solid ${SLOT_COLORS[idx]}30`,
                  }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0, background: SLOT_COLORS[idx], color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '13px' }}>{idx + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: SLOT_COLORS[idx], fontSize: '12px' }}>{it.category_name}{it.type_name ? ` › ${it.type_name}` : ''}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: '13px', color: C.textMain, fontWeight: 600, marginTop: '2px' }}>{it.serial_number || '—'}</div>
                      {it.ref_number && <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub, marginTop: '1px' }}>Ref: {it.ref_number}</div>}
                      <div style={{ fontSize: '11px', color: C.textSub, marginTop: '1px' }}>{it.item_id}</div>
                    </div>
                    <span style={{ background: '#F3E8FF', color: '#6D28D9', padding: '2px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>Packed</span>
                  </div>
                ))}
              </div>
              {selPkg.status === 'installed' && selPkg.mapped_client_name && (
                <div style={{ marginTop: '16px', background: '#F5F3FF', border: '1.5px solid #8B5CF6', borderRadius: '10px', padding: '12px 16px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 800, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                    📍 Installed At
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '14px', flexShrink: 0 }}>
                      {(selPkg.mapped_client_name || 'C')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, color: '#3B0764', fontSize: '14px' }}>{selPkg.mapped_client_name}</div>
                      <div style={{ fontSize: '11px', color: '#7C3AED', fontWeight: 600 }}>
                        {selPkg.mapped_client_ref}
                        {selPkg.mapped_client_contact && <span style={{ marginLeft: '8px' }}>· {selPkg.mapped_client_contact}</span>}
                      </div>
                      <div style={{ fontSize: '10px', marginTop: '3px', color: '#6D28D9', fontFamily: 'monospace' }}>
                        {selPkg.mapping_id} · Payment {selPkg.mapping_status === 'completed' ? '✓ Cleared' : '⏳ Pending'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {selPkg.notes && (
                <div style={{ marginTop: '16px', fontSize: '12px', color: C.textSub, background: C.primaryBg, borderRadius: '8px', padding: '10px 14px' }}>
                  <span style={{ fontWeight: 700, color: C.textMain }}>Notes: </span>{selPkg.notes}
                </div>
              )}
              <div style={{ marginTop: '10px', fontSize: '11px', color: C.textSub }}>Created: {selPkg.created_at}</div>
            </div>
            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => openHistory(selPkg)} style={{ ...btnPrimary, background: `linear-gradient(135deg,${C.info},#2563EB)` }}>📋 Dispatch History</button>
                <button onClick={() => { setSelPkg(selPkg); setModal('delete'); }} style={{ ...btnGhost, color: C.danger, borderColor: C.danger }}>Delete</button>
              </div>
              <button onClick={() => setModal(null)} style={btnGhost}>Close</button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Dispatch History Modal ── */}
      {modal === 'history' && selPkg && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '500px' }}>
            <div style={{ padding: '20px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '3px', height: '18px', background: C.info, borderRadius: '2px' }} />
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>Dispatch History</div>
                  <div style={{ fontSize: '12px', color: C.textSub, fontFamily: 'monospace' }}>{selPkg.package_id}{selPkg.name ? ` — ${selPkg.name}` : ''}</div>
                </div>
              </div>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>

            <div style={{ padding: '20px 28px', maxHeight: '65vh', overflowY: 'auto' }}>
              {historyLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: C.textSub }}>Loading history…</div>
              ) : dispatchHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>📋</div>
                  <div style={{ fontSize: '13px', color: C.textSub }}>
                    No dispatch history yet.<br />
                    This package will be dispatched when an installer scans its barcode.
                  </div>
                </div>
              ) : (
                <div style={{ position: 'relative', paddingLeft: '20px' }}>
                  {/* Vertical timeline line */}
                  <div style={{ position: 'absolute', left: '7px', top: '10px', bottom: '10px', width: '2px', background: C.border, borderRadius: '1px' }} />

                  {dispatchHistory.map((ev, idx) => {
                    const isClientEvent = ev.event_type === 'client';
                    const isLastDispatch = !isClientEvent && idx === dispatchHistory.length - 1;
                    const hasClientAfter = !isClientEvent && dispatchHistory[idx + 1]?.event_type === 'client';
                    // last installer entry before client = "currently held by installer when mapped"
                    const isCurrent = isLastDispatch && !hasClientAfter;

                    if (isClientEvent) {
                      return (
                        <div key={`client-${idx}`} style={{ position: 'relative', marginBottom: 0 }}>
                          {/* Arrow connector */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0 8px 0' }}>
                            <div style={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${C.border}, #8B5CF6)`, marginLeft: '0' }} />
                            <span style={{ fontSize: '11px', color: '#7C3AED', fontWeight: 700, whiteSpace: 'nowrap' }}>→ Installed at client</span>
                            <div style={{ flex: 1, height: '1px', background: `linear-gradient(90deg, #8B5CF6, ${C.border})` }} />
                          </div>

                          {/* Client card */}
                          <div style={{
                            background: '#F5F3FF', border: '2px solid #8B5CF6',
                            borderRadius: '12px', padding: '14px 16px',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                  width: '34px', height: '34px', borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
                                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontWeight: 800, fontSize: '14px', flexShrink: 0,
                                }}>
                                  {(ev.client_name || 'C')[0].toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 800, color: '#3B0764', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {ev.client_name}
                                    <span style={{ background: '#7C3AED', color: '#fff', fontSize: '9px', padding: '2px 7px', borderRadius: '10px', fontWeight: 800 }}>INSTALLED</span>
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#7C3AED', fontWeight: 600 }}>
                                    {ev.client_ref}
                                    {ev.client_contact && <span style={{ marginLeft: '8px', color: '#6D28D9' }}>· {ev.client_contact}</span>}
                                  </div>
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '11px', color: '#7C3AED', fontWeight: 600 }}>📅 {ev.dispatched_at}</div>
                                <div style={{ fontSize: '10px', color: '#A78BFA', marginTop: '2px', fontFamily: 'monospace' }}>{ev.mapping_id}</div>
                              </div>
                            </div>
                            <div style={{ fontSize: '11px', color: '#6D28D9', marginLeft: '44px', background: '#EDE9FE', borderRadius: '6px', padding: '5px 10px', display: 'inline-block' }}>
                              Payment: <strong>{ev.mapping_status === 'completed' ? '✓ Paid' : '⏳ Pending'}</strong>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={ev.id} style={{ position: 'relative', marginBottom: idx < dispatchHistory.length - 1 ? '22px' : 0 }}>
                        {/* Timeline dot */}
                        <div style={{
                          position: 'absolute', left: '-20px', top: '10px',
                          width: '14px', height: '14px', borderRadius: '50%',
                          background: isCurrent ? C.info : (hasClientAfter ? '#7C3AED' : C.border),
                          border: `2px solid ${isCurrent ? C.info : (hasClientAfter ? '#7C3AED' : '#E5E7EB')}`,
                          zIndex: 1,
                        }} />

                        <div style={{
                          background: isCurrent ? C.infoBg : (hasClientAfter ? '#F5F3FF' : '#F9FAFB'),
                          border: `1px solid ${isCurrent ? C.info + '40' : (hasClientAfter ? '#8B5CF640' : C.border)}`,
                          borderRadius: '10px', padding: '12px 16px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{
                                width: '28px', height: '28px', borderRadius: '50%',
                                background: hasClientAfter ? '#7C3AED' : (isCurrent ? C.info : '#6B7280'),
                                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: '11px', flexShrink: 0,
                              }}>
                                {(ev.installer_name || 'I')[0].toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, color: hasClientAfter ? '#5B21B6' : (isCurrent ? C.infoText : C.textMain), fontSize: '13px' }}>
                                  {ev.installer_name}
                                  {isCurrent && (
                                    <span style={{ marginLeft: '8px', background: C.info, color: '#fff', fontSize: '9px', padding: '2px 7px', borderRadius: '10px', fontWeight: 800 }}>WITH INSTALLER</span>
                                  )}
                                  {hasClientAfter && (
                                    <span style={{ marginLeft: '8px', background: '#7C3AED', color: '#fff', fontSize: '9px', padding: '2px 7px', borderRadius: '10px', fontWeight: 800 }}>INSTALLER</span>
                                  )}
                                </div>
                                <div style={{ fontSize: '10px', color: C.textSub }}>@{ev.installer_user}</div>
                              </div>
                            </div>
                            <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 600, flexShrink: 0 }}>
                              📅 {ev.dispatched_at}
                            </div>
                          </div>
                          <div style={{ fontSize: '11px', color: hasClientAfter ? '#7C3AED' : (isCurrent ? C.infoText : C.textSub), marginTop: '4px', marginLeft: '36px' }}>
                            {hasClientAfter ? 'Dispatched to installer · mapped to client below' : isCurrent ? 'Currently with this installer' : `Dispatched on ${ev.dispatched_at}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} style={btnGhost}>Close</button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Barcode Modal ── */}
      {modal === 'barcode' && barcodeData && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '420px' }}>
            <div style={{ padding: '20px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
                <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>Package Barcode</span>
              </div>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>

            <div style={{ padding: '24px 28px', textAlign: 'center' }}>
              {/* Package info */}
              <div style={{ marginBottom: '16px' }}>
                {barcodeData.packageId && (
                  <div style={{ fontFamily: 'monospace', fontSize: '12px', color: C.primary, fontWeight: 700, marginBottom: '4px' }}>{barcodeData.packageId}</div>
                )}
                {barcodeData.label && (
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.textMain }}>{barcodeData.label}</div>
                )}
              </div>

              {/* Barcode canvas */}
              <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '8px', display: 'inline-block', width: '100%', boxSizing: 'border-box' }}>
                <canvas ref={barcodeCanvasRef} style={{ width: '100%', height: 'auto', display: 'block' }} />
              </div>

              <div style={{ marginTop: '10px', fontFamily: 'monospace', fontSize: '11px', color: C.textSub, letterSpacing: '0.5px' }}>
                {barcodeData.serial}
              </div>
            </div>

            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={downloadBarcode} style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                ⬇ Download PNG
              </button>
              <button onClick={printBarcode} style={{ ...btnGhost, display: 'flex', alignItems: 'center', gap: '6px' }}>
                🖨 Print
              </button>
              <button onClick={() => setModal(null)} style={btnGhost}>Close</button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Delete Modal ── */}
      {modal === 'delete' && selPkg && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '420px' }}>
            <MHeader title="Delete Package" onClose={() => setModal(null)} />
            <div style={{ padding: '20px 28px' }}>
              <div style={{ background: C.warningBg, border: `1px solid ${C.warning}30`, borderRadius: '10px', padding: '14px', marginBottom: '12px', fontSize: '13px', color: C.warningText }}>
                ⚠ Deleting <strong style={{ fontFamily: 'monospace' }}>{selPkg?.package_id}</strong> will restore all <strong>{selPkg?.item_count}</strong> packed item(s) back to <strong>In Stock</strong>.
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: C.textSub }}>This action is permanent and cannot be undone.</p>
            </div>
            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
              <button onClick={confirmDelete} disabled={saving} style={{ ...btnPrimary, background: C.danger, boxShadow: 'none' }}>
                {saving ? 'Deleting…' : 'Delete Package'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Assign to installer ── */}
      {assignPkg && (
        <Overlay>
          <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '400px' }}>
            <div style={{ padding: '17px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: C.textMain }}>Assign to Installer</div>
                <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub }}>{assignPkg.package_id} · {assignPkg.item_count} device(s)</div>
              </div>
              <button onClick={() => setAssignPkg(null)} style={{ background: 'none', border: 'none', fontSize: '19px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>
            <div style={{ padding: '18px 22px' }}>
              {assignPkg.assigned_installer_name && (
                <div style={{ fontSize: '12px', color: C.textSub, marginBottom: '10px' }}>
                  Currently with <strong style={{ color: '#1D4ED8' }}>{assignPkg.assigned_installer_name}</strong> — reassigning moves it.
                </div>
              )}
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>Installer *</label>
              <select value={assignInstaller} onChange={e => { setAssignInstaller(e.target.value); setAssignErr(''); }}
                style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box', border: `1.5px solid ${C.border}`, borderRadius: '9px', fontSize: '13px', outline: 'none', background: C.white }}>
                <option value="">— Select installer —</option>
                {installers.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.user_id})</option>)}
              </select>
              <div style={{ marginTop: '12px', padding: '10px 13px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '9px', fontSize: '11px', color: '#1D4ED8', lineHeight: 1.5 }}>
                ℹ The package moves to this installer's hands — they can then map it to a client (which comes back for your pricing).
              </div>
              {assignErr && <div style={{ marginTop: '10px', color: C.danger, fontSize: '12px', fontWeight: 600 }}>{assignErr}</div>}
            </div>
            <div style={{ padding: '13px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '9px', justifyContent: 'flex-end' }}>
              <button onClick={() => setAssignPkg(null)} disabled={assigning} style={btnGhost}>Cancel</button>
              <button onClick={confirmAssign} disabled={assigning} style={{ padding: '9px 22px', background: assigning ? '#9CA3AF' : 'linear-gradient(135deg,#2563EB,#1D4ED8)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: assigning ? 'not-allowed' : 'pointer' }}>
                {assigning ? 'Assigning…' : '👷 Assign'}
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </Layout>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SmBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer' }}>
      {label}
    </button>
  );
}

function Overlay({ children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
      {children}
    </div>
  );
}

function MHeader({ title, onClose }) {
  return (
    <div style={{ padding: '20px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
        <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputBase  = { width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none', boxSizing: 'border-box', background: C.white };
const card       = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const modalBox   = { background: C.white, borderRadius: '16px', width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}` };
const th         = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td         = { padding: '13px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell  = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const selS       = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary = { padding: '10px 20px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: `0 3px 10px rgba(245,166,35,0.3)` };
const btnGhost   = { padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
